/**
 * Auth fields stored in cookies (not localStorage).
 * Keys: sessionToken, userType, userId, userName, licenseType, email, supplierId (optional)
 */

const AUTH_COOKIE_KEYS = [
  "sessionToken",
  "userType",
  "globalUserId",
  "userName",
  "licenseType",
  "email",
  "organizationId",
  "membershipId",
  "supplierId",
] as const;

export type AuthCookieKey = (typeof AUTH_COOKIE_KEYS)[number];

const MAX_AGE_SEC = 60 * 60 * 24 * 30; // 30 days

function cookieSuffix(): string {
  if (typeof window === "undefined") return "";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  return `; path=/; SameSite=Lax; max-age=${MAX_AGE_SEC}${secure}`;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Some environments store userName JSON-encoded (e.g. `"Jane Doe"` with literal quotes).
 * Strip one pair of surrounding ASCII double quotes so UI shows Jane Doe.
 */
function stripSurroundingAsciiDoubleQuotes(value: string): string {
  const t = value.trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    return t.slice(1, -1);
  }
  return value;
}

export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${escapeRegExp(name)}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export function setCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}${cookieSuffix()}`;
}

export function removeCookie(name: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; path=/; max-age=0`;
}

export function getAuthCookie(key: AuthCookieKey): string | null {
  const raw = getCookie(key);
  if (raw === null) return null;
  if (key === "userName") {
    return stripSurroundingAsciiDoubleQuotes(raw);
  }
  return raw;
}

export function setAuthCookie(key: AuthCookieKey, value: string): void {
  const normalized =
    key === "userName" ? stripSurroundingAsciiDoubleQuotes(String(value)) : String(value);
  setCookie(key, normalized);
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(key);
    if (key === "email") localStorage.removeItem("userEmail");
  }
}

export interface AuthCookieValues {
  sessionToken: string;
  
  globalUserId: string;
  membershipId: string;
  organizationId: string;

  userType: string;
  userName: string;
  licenseType: string;
  email: string;
  /** Supplier / vendor account id when not acting as SIGNAGE_FIRM (also try cookie `supplier_id` + localStorage). */
  supplierId?: string;
}

export function setAuthCookies(values: Partial<AuthCookieValues>): void {
  (Object.entries(values) as [AuthCookieKey, string][]).forEach(([k, v]) => {
    if (v !== undefined && v !== null) setAuthCookie(k, String(v));
  });
  clearLegacyAuthLocalStorage();
}

/** Remove legacy localStorage copies of auth fields (migration from localStorage to cookies). */
export function clearLegacyAuthLocalStorage(): void {
  if (typeof localStorage === "undefined") return;
  const legacy = [
    ...AUTH_COOKIE_KEYS,
    "userEmail",
  ] as const;
  legacy.forEach((k) => localStorage.removeItem(k));
}

export function clearAuthCookies(): void {
  AUTH_COOKIE_KEYS.forEach(removeCookie);
  removeCookie("supplier_id");
  clearLegacyAuthLocalStorage();
}

/**
 * Persist supplier / firm user id for `resolveEmailTemplateBrandingUserId` and similar flows.
 * Sets auth cookie `supplierId` and legacy `supplier_id` (same value).
 * Call when the vendor id is known from a public payload (e.g. design review portal project).
 */
export function setSupplierIdContextFromValue(supplierUserId: string): void {
  const id = String(supplierUserId || "").trim();
  if (!id) return;
  setAuthCookie("supplierId", id);
  setCookie("supplier_id", id);
}

/**
 * Organization id for `GET /public/email-templates/organizations/:id`.
 * - If `explicitOrganizationId` is set (e.g. seller/vendor org), it wins.
 * - Signage seller login → session `organizationId` (they are the seller).
 * - Agent / brokerage / brokerage team member → seller org only
 *   (`supplierOrganizationId` / supplier cookies). Never buyer session `organizationId`.
 */
export function resolveEmailTemplateBrandingOrganizationId(
  explicitOrganizationId?: string | null
): string {
  const ex = String(explicitOrganizationId ?? "").trim();
  if (ex) return ex;

  const cookieOrg = String(getAuthCookie("organizationId") || "").trim();
  const lt = String(getAuthCookie("licenseType") || "").trim().toUpperCase();
  if (
    (lt === "SIGNAGE_FIRM" || lt === "SIGNAGE_TEAM_MEMBER") &&
    cookieOrg
  ) {
    return cookieOrg;
  }

  // Buyer licenses: email branding is the seller (signage) org, not the buyer cookie org.
  return (
    (typeof localStorage !== "undefined"
      ? String(localStorage.getItem("supplierOrganizationId") || "").trim()
      : "") ||
    String(getCookie("supplierOrganizationId") || "").trim() ||
    String(getAuthCookie("supplierId") || "").trim() ||
    String(getCookie("supplier_id") || "").trim() ||
    (typeof localStorage !== "undefined" ? String(localStorage.getItem("supplierId") || "").trim() : "")
  );
}

/** @deprecated Prefer {@link resolveEmailTemplateBrandingOrganizationId} — value is organization id. */
export function resolveEmailTemplateBrandingUserId(explicitUserId?: string | null): string {
  return resolveEmailTemplateBrandingOrganizationId(explicitUserId);
}

/** Same-origin app path from `?redirect=` (email View links). Rejects protocol-relative / absolute URLs. */
export function getSafeInternalRedirect(raw: unknown): string {
  let value = String(raw ?? "").trim();
  if (!value) return "";
  try {
    value = decodeURIComponent(value);
  } catch {
    /* already decoded */
  }
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("://")) return "";
  if (/[\n\r\\]/.test(value)) return "";
  return value;
}

/** Login URL that returns the user to the requested app path after auth. */
export function unauthenticatedLoginPath(pathname: string, search = ""): string {
  const safe = getSafeInternalRedirect(`${pathname}${search || ""}`);
  return safe ? `/login?redirect=${encodeURIComponent(safe)}` : "/login";
}
